// Web-mode progress polling. The desktop app follows backend progress via Tauri
// events; the webserver has no event channel (its `emit` is a no-op), so web
// consumers poll an `/api/get_*_progress` endpoint that returns the latest
// payload (or null when nothing is in flight) and synthesize the same UI.
//
// Mirrors the polling half of `replayEvents.onReplayEvent`, generalised for any
// progress endpoint. Returns an unsubscribe fn. Used by the export / diag / trend
// progress UIs on web.

/**
 * Poll `url` immediately and then every `intervalMs`, calling `cb` with the
 * parsed JSON body of each successful response (`null` when the server reports
 * nothing in flight). Transient fetch/parse errors are swallowed so a blip
 * doesn't tear down the poll. Returns a function that stops polling.
 */
export function pollProgress<T>(
  url: string,
  cb: (value: T | null) => void,
  intervalMs = 700,
): () => void {
  let stopped = false;
  const tick = async () => {
    try {
      const res = await fetch(url);
      if (stopped || !res.ok) return;
      cb((await res.json()) as T | null);
    } catch {
      // Ignore transient errors; the next tick retries.
    }
  };
  void tick(); // show progress without waiting a full interval
  const id = setInterval(tick, intervalMs);
  return () => {
    stopped = true;
    clearInterval(id);
  };
}
